import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar, CompanyCard, FilterBar, CartContents, useAuth } from "../components/Shared";
import AllergyWarning from "../components/AllergyWarning";
import { CITY } from "../mock";
import { diagLog, diagError, isDiagnosticsMode } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Heart, Sparkles, Check, MapPin, Star } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { fetchVendors, fetchVendorDetail, fetchPromotions, mySubscriptions, fetchPlans, favMeal, unfavMeal, updateSubscription } from "../lib/api";
import WeeklySubscription from "../components/WeeklySubscription";
import { getMockSub, clearMockSub } from "../lib/mockSub";

// Compact Plan Card for vendor page preview - CLICKABLE
function CompactPlanCard({ plan, vendor, isPopular, isSelected, onSelect }) {
  const pricePerMeal = vendor?.plan_prices?.[plan.id];
  
  return (
    <button
      onClick={onSelect}
      className={`
        relative w-full p-4 rounded-2xl text-center transition-all duration-300 cursor-pointer
        ${isSelected
          ? 'border-2 border-[#2a7d4f] bg-[#2a7d4f] text-white shadow-xl scale-[1.03]'
          : isPopular 
            ? 'border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50/50 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-amber-400' 
            : 'border border-neutral-100 bg-white hover:shadow-lg hover:scale-[1.02] hover:border-neutral-200'
        }
      `}
      data-testid={`plan-card-${plan.meals}`}
    >
      {/* Popular Badge */}
      {isPopular && !isSelected && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-amber-500 to-amber-400 text-white text-[9px] px-2 py-0.5 shadow-sm border-0">
            <Sparkles size={8} className="mr-0.5" />
            Popular
          </Badge>
        </div>
      )}
      
      {/* Selected Checkmark */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center">
          <Check size={12} className="text-black" />
        </div>
      )}
      
      <div className={`text-3xl font-bold ${isSelected ? 'text-white' : 'text-neutral-900'}`}>{plan.meals}</div>
      <div className={`text-xs mt-0.5 ${isSelected ? 'text-white/70' : 'text-neutral-400'}`}>meals/week</div>
      <div className={`mt-2 text-lg font-semibold ${isSelected ? 'text-white' : 'text-neutral-900'}`}>
        ${pricePerMeal?.toFixed(2) || '—'}
        <span className={`text-xs font-normal ${isSelected ? 'text-white/60' : 'text-neutral-400'}`}>/meal</span>
      </div>
    </button>
  );
}

function ActiveSubscriptionBanner({ sub }){
  const [vendor, setVendor] = React.useState(null);
  const [planLabel, setPlanLabel] = React.useState("");
  React.useEffect(()=>{ (async()=>{
    try{ const d = await fetchVendorDetail(sub.company_id); setVendor(d.company); }catch{}
    try{ const plans = await fetchPlans(); const found = plans.find(p=> p.id===sub.plan_id); setPlanLabel(found? found.label : sub.plan_id); }catch{ setPlanLabel(sub.plan_id); }
  })(); },[sub]);
  const status = (sub.status||'active').toUpperCase();
  const selection = sub.current_week?.skipped ? 'Skipped' : (sub.current_week?.has_selection? 'Selected':'Pending');
  const deadline = sub.current_week?.selection_deadline? new Date(sub.current_week.selection_deadline).toLocaleDateString() : null;

  async function cancelSub(){
    try{
      if(!sub.id){ clearMockSub(); window.location.reload(); return; }
      await updateSubscription(sub.company_id, sub.id, { status: 'canceled' });
      window.location.reload();
    }catch{}
  }

  return (
    <div className="mb-4 rounded-lg border p-3 bg-green-50 border-green-200">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{vendor?.name || sub.company_id}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{status}</span>
          </div>
          <div className="text-xs text-neutral-500 mt-0.5">
            {planLabel || sub.plan_id}
            {' · '}This week: {selection}
            {deadline ? ` · deadline ${deadline}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Link to="/dashboard" className="text-sm underline">Manage</Link>
          <button onClick={cancelSub} className="text-sm text-red-700 underline">Cancel</button>
        </div>
      </div>
    </div>
  );
}


export function Home() {
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [errorVendors, setErrorVendors] = useState(null);
  const navigate = useNavigate();
  const { user, isPilot, pilotVendorId, configLoading } = useAuth();
  const [subs, setSubs] = useState([]);

  async function loadHomeVendors(city){
    const cityLabel = city || CITY.label;
    diagLog('Home', 'vendors fetch start', { city: cityLabel, ts: new Date().toISOString() });
    setLoadingVendors(true);
    setErrorVendors(null);
    try{
      const data = await fetchVendors(cityLabel);
      diagLog('Home', 'vendors fetch success', { count: data?.length||0, ts: new Date().toISOString() });
      setVendors(Array.isArray(data)? data: []);
    }catch(err){
      diagError('Home', 'vendors fetch error', err);
      setErrorVendors(err?.message || 'Failed to load vendors');
      setVendors([]);
    }finally{
      setLoadingVendors(false);
    }
  }

  // Load vendors in non-pilot mode, and in pilot mode for logged-in bound customers
  useEffect(() => {
    if (!configLoading && !isPilot) {
      loadHomeVendors(user?.location || CITY.label);
    } else if (!configLoading && isPilot && user?.vendor_id && pilotVendorId) {
      loadHomeVendors(user?.location || CITY.label);
    }
  }, [user, configLoading, isPilot, pilotVendorId]);
  
  useEffect(() => {
    const mock = getMockSub();
    if (mock) setSubs([mock]);
    else if (user) mySubscriptions().then(setSubs).catch(()=>setSubs([]));
  }, [user]);

  const featured = vendors.slice(0, 6);

  // Show loading state while config is loading to prevent UI flicker
  if (configLoading) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="rounded-xl border bg-white p-6 md:p-10 animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-2/3 mb-3" />
            <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
            <div className="h-4 bg-neutral-200 rounded w-3/4 mb-6" />
            <div className="h-10 bg-neutral-200 rounded-lg w-64" />
          </div>
        </div>
      </div>
    );
  }

  // ===== PILOT MODE ROUTING =====
  if (isPilot) {
    // Superadmin — never redirect, let them view freely
    if (user && user.role === 'superadmin') {
      navigate('/superadmin', { replace: true });
      return null;
    }

    // Vendor/company/admin accounts always go to their dashboard, never the customer flow
    if (user && (user.role === 'vendor' || user.role === 'company' || user.role === 'admin')) {
      navigate('/admin', { replace: true });
      return null;
    }

    // Logged in but NOT bound → redirect to pilot-not-joined
    if (user && !user.vendor_id) {
      navigate('/pilot-not-joined', { replace: true });
      return null;
    }
    
    // Logged out → show invite-only landing page
    if (!user) return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />

        {/* SECTION 1: Hero — two-column on desktop */}
        <div className="bg-gradient-to-br from-[#f5efe6] to-white">
          <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

              {/* Left column: copy + stats */}
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#2a7d4f]/10 text-[#2a7d4f] text-sm font-medium mb-6">
                  <MapPin size={14} />
                  Austin, TX · Pilot launching now
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-black leading-tight">
                  Austin's first<br />meal prep marketplace
                </h1>
                <p className="mt-4 text-lg text-neutral-600 max-w-lg">
                  Browse local meal prep chefs, choose a weekly plan, and get
                  fresh healthy meals every week — no cooking required.
                </p>
                <ul className="mt-6 space-y-2">
                  {[
                    'Pick 4–14 meals per week from local prep chefs',
                    'Choose your meals fresh each week, skip anytime',
                    'Pickup in Austin — no delivery minimums',
                  ].map(b => (
                    <li key={b} className="flex items-center gap-2 text-neutral-700">
                      <span className="w-5 h-5 rounded-full bg-[#2a7d4f] flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-white" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-6">
                  {[
                    { value: '5+', label: 'Local chefs' },
                    { value: '20+', label: 'Meal plans' },
                    { value: '100%', label: 'Fresh weekly' },
                  ].map(({ value, label }) => (
                    <div key={label} className="flex items-baseline gap-1.5">
                      <span className="text-2xl font-bold text-[#2a7d4f]">{value}</span>
                      <span className="text-sm text-neutral-500">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right column: vendor spotlight + testimonial */}
              <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="rounded-2xl overflow-hidden border border-neutral-100 bg-white shadow-sm">
                  <img
                    src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=300&fit=crop&auto=format"
                    alt="Healthy meal prep bowls"
                    className="w-full h-44 object-cover"
                  />
                  <div className="p-4">
                    <div className="text-xs font-medium text-[#2a7d4f] uppercase tracking-wide mb-1">Featured chef</div>
                    <div className="font-semibold text-neutral-900 text-base mb-1">Clean Eats ATX</div>
                    <p className="text-sm text-neutral-500 mb-3">Mediterranean-inspired meal prep — grilled proteins, roasted veggies, whole grains.</p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(i => (
                        <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                      <span className="text-xs text-neutral-500 ml-1">4.9 · 38 reviews</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#2a7d4f" className="mb-2 opacity-30" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.3 7C8.5 8.3 6.8 10.5 6.8 13c0 1.1.4 2 1.2 2.6.6.5 1.3.7 2 .7 1.7 0 2.9-1.3 2.9-2.8 0-1.6-1.1-2.7-2.6-2.7-.2 0-.5 0-.7.1.3-1.4 1.4-2.6 3-3.3L11.3 7zm7 0c-2.8 1.3-4.5 3.5-4.5 6 0 1.1.4 2 1.2 2.6.6.5 1.3.7 2 .7 1.7 0 2.9-1.3 2.9-2.8 0-1.6-1.1-2.7-2.6-2.7-.2 0-.5 0-.7.1.3-1.4 1.4-2.6 3-3.3L18.3 7z"/>
                  </svg>
                  <p className="text-neutral-700 text-sm leading-relaxed mb-3">
                    "I've been getting my meals from PrepMate every week — the variety is incredible and it saves me hours on Sundays."
                  </p>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#2a7d4f]/10 flex items-center justify-center text-[#2a7d4f] font-semibold text-xs">S</div>
                    <div>
                      <div className="text-sm font-medium text-neutral-900">Sarah M.</div>
                      <div className="text-xs text-neutral-500">South Austin</div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* SECTION 2: How it works */}
        <div className="mx-auto max-w-6xl px-4 py-12">
          <h2 className="text-xl font-semibold text-neutral-900 mb-6">How it works</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { step: '1', title: 'Choose a plan', desc: 'Pick how many meals per week — from 4 to 14.' },
              { step: '2', title: 'Pick your meals', desc: 'Browse the weekly menu and select what you want each week.' },
              { step: '3', title: 'Pickup fresh', desc: 'Collect your meals at the Austin pickup location weekly.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="rounded-2xl bg-white border border-neutral-100 p-5 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-[#2a7d4f] text-white flex items-center justify-center font-bold text-sm mb-3">
                  {step}
                </div>
                <div className="font-semibold text-neutral-900 mb-1">{title}</div>
                <div className="text-sm text-neutral-500">{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: Invite CTA */}
        <div className="mx-auto max-w-6xl px-4 pb-16">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 max-w-lg mx-auto text-center">
            <div className="flex items-center justify-center gap-2 text-amber-800 mb-2">
              <Sparkles size={16} />
              <span className="font-semibold text-sm">Invite-only pilot</span>
            </div>
            <p className="text-sm text-amber-700 mb-5">
              We're currently in a private pilot. Use your invite link to join,
              or sign in if you already have an account.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/auth')}
                className="h-11 px-7 bg-[#2a7d4f] text-white hover:bg-[#236840] rounded-xl text-sm font-semibold"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate('/pilot-not-joined')}
                variant="outline"
                className="h-11 px-7 rounded-xl text-sm font-semibold border-2"
              >
                Enter invite code
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== PILOT MODE: Logged-in customer browse page =====
  if (isPilot) {
    const pilotVendor = vendors.find(v => v.id === pilotVendorId) || vendors[0];
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          {subs?.length > 0 && <ActiveSubscriptionBanner sub={subs[0]} />}
          <h2 className="text-xl font-semibold mb-4">Your meal prep chef</h2>
          {loadingVendors ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-2xl bg-white border border-neutral-100 animate-pulse">
                <div className="aspect-[16/10] bg-neutral-200 rounded-t-2xl" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-neutral-200 rounded w-2/3" />
                  <div className="h-3 bg-neutral-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ) : pilotVendor ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <CompanyCard vendor={pilotVendor} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ===== NON-PILOT MODE: Normal marketplace =====
  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        {user && subs?.length>0 && (
          <ActiveSubscriptionBanner sub={subs[0]} />
        )}
        <div className="rounded-xl border bg-[#f5efe6] p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-black">Austin meal prep, delivered weekly</h1>
          <p className="mt-2 text-neutral-700 max-w-2xl">Browse local meal prep companies, pick your weekly plan (4–14 meals), set vendor-defined per‑meal pricing, and choose delivery or pickup days.</p>
          <div className="mt-6 flex gap-2">
            <Input placeholder="Search by vendor, cuisine, or diet" className="bg-white" />
            <Button onClick={() => navigate("/browse")} className="bg-[#2a7d4f] text-white hover:bg-[#236840]">Search</Button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Featured in {user?.location || CITY.label}</h2>
          <Link to="/browse" className="text-sm text:black/80 hover:text-black">See all</Link>
        </div>

        {loadingVendors && (
          <div className="mt-4 text-sm text-neutral-700">Loading vendors…</div>
        )}
        {!loadingVendors && errorVendors && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-center justify-between">
            <span>Failed to load vendors, retry</span>
            <Button variant="outline" className="bg-white" onClick={()=>loadHomeVendors(user?.location || CITY.label)}>Retry</Button>
          </div>
        )}
        {!loadingVendors && !errorVendors && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.length === 0 ? (
              <div className="text-sm text-neutral-600">
                No vendors in Austin yet.{" "}
                <Link to="/vendors/apply" className="text-[#2a7d4f] font-medium hover:underline">
                  Be our first vendor →
                </Link>
              </div>
            ) : (
              featured.map(v => <CompanyCard key={v.id} vendor={v} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function Browse() {
  const { user, isPilot, configLoading } = useAuth();
  const navigate = useNavigate();
  const [diet, setDiet] = useState("all");
  const [sort, setSort] = useState("rating");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function loadVendors(city){
    const cityLabel = city || CITY.label;
    diagLog('Browse', 'vendors fetch start', { city: cityLabel, ts: new Date().toISOString() });
    setLoading(true); setError(null);
    try{
      const data = await fetchVendors(cityLabel);
      diagLog('Browse', 'vendors fetch success', { count: data?.length||0, ts: new Date().toISOString() });
      setVendors(Array.isArray(data)? data: []);
    }catch(err){
      diagError('Browse', 'vendors fetch error', err);
      setError(err?.message || 'Failed to load vendors');
      setVendors([]);
    }finally{
      setLoading(false);
    }
  }

  useEffect(() => { if(!isPilot && !configLoading){ loadVendors(user?.location || CITY.label); } }, [user, isPilot, configLoading]);

  useEffect(() => {
    if (!configLoading && !user) navigate('/auth', { replace: true });
  }, [user, configLoading, navigate]);

  const filtered = useMemo(() => {
    let list = [...vendors];
    if (diet !== "all") list = list.filter(v => v.diets?.includes(diet));
    if (sort === "rating") list.sort((a,b) => (b.rating||0) - (a.rating||0));
    if (sort === "fastest") list.sort((a,b) => parseInt(a.eta) - parseInt(b.eta));
    if (sort === "reviews") list.sort((a,b) => (b.reviews||0) - (a.reviews||0));
    return list;
  }, [diet, sort, vendors]);

  // Show loading state while config is loading
  if (configLoading) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="animate-pulse mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="rounded-2xl border bg-white overflow-hidden">
                <div className="aspect-[16/10] bg-neutral-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-neutral-200 rounded w-3/4" />
                  <div className="h-3 bg-neutral-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (isPilot) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="rounded-xl border bg-white p-6">
            <h1 className="text-2xl font-semibold">Pilot mode</h1>
            <p className="text-sm text-neutral-600 mt-1">Browsing is disabled during the pilot.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <FilterBar diet={diet} setDiet={setDiet} sort={sort} setSort={setSort} />
        {loading && (<div className="mt-4 text-sm text-neutral-700">Loading vendors…</div>)}
        {!loading && error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-center justify-between">
            <span>Failed to load vendors, retry</span>
            <Button variant="outline" className="bg-white" onClick={()=>loadVendors(user?.location || CITY.label)}>Retry</Button>
          </div>
        )}
        {!loading && !error && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.length === 0 ? (
              <div className="text-sm text-neutral-600">
                No vendors match your filters.{" "}
                <Link to="/vendors/apply" className="text-[#2a7d4f] font-medium hover:underline">
                  Become a vendor →
                </Link>
              </div>
            ) : (
              filtered.map(v => <CompanyCard key={v.id} vendor={v} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function Company() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, setUser, isPilot, configLoading } = useAuth();
  
  const [vendor, setVendor] = useState(null);
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [activeForVendor, setActiveForVendor] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  
  // Track if we've already shown the redirect toast to prevent duplicates
  const redirectShownRef = useRef(false);

  useEffect(()=>{ fetchPlans().then(setPlans).catch(()=>setPlans([])); },[]);
  const mapping = useMemo(()=> Object.fromEntries(plans.map(p=>[p.id,p])),[plans]);

  useEffect(() => { 
    fetchVendorDetail(id).then(setVendor).catch(() => setVendor(null)); 
    fetchPromotions(id).then(setPromos).catch(()=>setPromos([])); 
  }, [id]);

  // Universal auth guard — applies in all modes
  useEffect(() => {
    if (configLoading) return;
    if (!user) navigate('/auth', { replace: true });
  }, [user, configLoading, navigate]);

  // Pilot mode vendor mismatch guard - redirect with toast
  useEffect(() => {
    if (configLoading) return;
    if (!isPilot) return;

    // Vendor/company/admin/superadmin accounts are never subject to customer redirect guards
    if (user && (user.role === 'vendor' || user.role === 'company' || user.role === 'admin' || user.role === 'superadmin')) return;

    // Handled by universal auth guard above
    if (!user) return;

    // If user is logged in but not bound to any vendor, redirect to pilot-not-joined
    if (user && !user.vendor_id) {
      if (redirectShownRef.current) return;
      redirectShownRef.current = true;
      navigate('/pilot-not-joined', { replace: true });
      return;
    }
    
    // If user is bound to a different vendor, redirect to their vendor
    if (user?.vendor_id && user.vendor_id !== id) {
      if (redirectShownRef.current) return;
      redirectShownRef.current = true;
      
      toast({
        title: "Redirecting to your vendor",
        description: "In pilot mode, you can only access your assigned vendor.",
      });
      navigate(`/company/${user.vendor_id}`, { replace: true });
      return;
    }
  }, [isPilot, user, id, configLoading, navigate, toast]);

  const v = vendor?.company;
  const offered = useMemo(()=> {
    if (!v) return [];
    const raw = v.plans_offered || [];
    if (raw.length === 0) {
      // Vendor hasn't configured plans_offered — show all available plans
      return plans.slice().sort((a, b) => a.meals - b.meals);
    }
    return raw.slice().sort((a,b)=>a-b).map(m=> mapping[`sub-${m}`]).filter(Boolean);
  }, [v, mapping, plans]);

  const favSet = new Set(user?.favorite_meals || []);
  async function toggleFavMeal(mealId){ 
    if(!user) return; 
    try{ 
      if(favSet.has(mealId)){ 
        await unfavMeal(mealId); 
        setUser({ ...user, favorite_meals: (user.favorite_meals||[]).filter(x=>x!==mealId) }); 
      } else { 
        await favMeal(mealId); 
        setUser({ ...user, favorite_meals: [...(user.favorite_meals||[]), mealId] }); 
      } 
    }catch{} 
  }

  // Detect active subscription for this vendor
  useEffect(()=>{
    let mounted = true;
    (async ()=>{
      if (!user || !v) return;
      try{ 
        const subs = await mySubscriptions(); 
        const found = subs.find(s=> (s.status||'')==='active' && s.company_id===v.id); 
        if(mounted){ 
          setActiveForVendor(found||null); 
        } 
      }catch{}
    })();
    return ()=>{ mounted=false };
  },[user, v]);

  // Show loading state while config is loading
  if (configLoading) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="animate-pulse space-y-4">
            <div className="rounded-3xl overflow-hidden bg-white border border-neutral-100">
              <div className="h-56 md:h-72 bg-neutral-200" />
              <div className="p-6 space-y-3">
                <div className="h-6 bg-neutral-200 rounded w-1/2" />
                <div className="h-4 bg-neutral-200 rounded w-3/4" />
              </div>
            </div>
            <div className="rounded-3xl bg-white border border-neutral-100 p-6">
              <div className="h-5 bg-neutral-200 rounded w-40 mb-4" />
              <div className="grid grid-cols-3 gap-4">
                {[1,2,3].map(i => <div key={i} className="h-24 bg-neutral-200 rounded-2xl" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="rounded-3xl overflow-hidden bg-white border border-neutral-100">
            <div className="h-56 md:h-72 bg-neutral-200" />
            <div className="p-6 space-y-3">
              <div className="h-6 bg-neutral-200 rounded w-1/2" />
              <div className="h-4 bg-neutral-200 rounded w-3/4" />
            </div>
          </div>
          <div className="rounded-3xl bg-white border border-neutral-100 p-6">
            <div className="h-5 bg-neutral-200 rounded w-40 mb-4" />
            <div className="grid grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-24 bg-neutral-200 rounded-2xl" />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const showSelect = !!(user && ((user.active_subscription && user.active_subscription.company_id === v.id) || activeForVendor));

  return (
    <div className="min-h-screen bg-neutral-50/50">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Hero Card */}
        <div className="rounded-3xl overflow-hidden bg-white shadow-sm border border-neutral-100">
          <div className="relative h-56 md:h-72 overflow-hidden bg-gradient-to-br from-neutral-200 to-neutral-300">
            <img loading="lazy" src={v.image} alt={v.name} className="h-full w-full object-cover" onError={e => { e.currentTarget.style.display = 'none'; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            
            {/* Vendor Name Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">{v.name}</h1>
              <div className="text-sm text-white/90 mt-1">{(v.cuisines||[]).join(", ")} • {(v.rating||0).toFixed(1)} ({v.reviews})</div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            {/* Pilot Mode: Pickup Badge & Intro */}
            {isPilot && (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-2xl">
                  <MapPin size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Pickup Only — Austin, TX</span>
                </div>
                
                <div className="p-5 bg-neutral-50 rounded-2xl">
                  <p className="text-neutral-600 leading-relaxed">
                    Choose a weekly meal plan, pick your meals each week, and pay once. 
                    Pick up your fresh meals at our Austin location on your selected day. 
                    Skip or pause anytime.
                  </p>
                </div>
              </>
            )}
            
            {/* Non-pilot: Show full details */}
            {!isPilot && (
              <div className="space-y-2 text-neutral-600 text-sm">
                <p>{v.description} • Hours: {v.hours}</p>
                <p>Delivery: {(v.delivery_days||[]).join(', ') || '—'} • Pickup: {(v.pickup_days||[]).join(', ') || '—'}</p>
                {(v.delivery||[]).includes('weekly') && v.delivery_fee && (
                  <p>Delivery fee ${v.delivery_fee}{v.delivery_radius_miles? ` within ${v.delivery_radius_miles}mi`:''}</p>
                )}
              </div>
            )}

            <p className="text-xs text-neutral-400 leading-relaxed">
              This vendor is an independent business. PrepMate is a technology marketplace and does not prepare, handle, or distribute food products. Allergen and nutritional information is provided by the vendor and has not been independently verified.{" "}
              <a href="/disclaimer" className="underline hover:text-neutral-600">Learn more</a>.
            </p>
            
            {promos.length > 0 && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 text-sm font-medium rounded-full">
                <Sparkles size={14} />
                {promos[0].percent}% off first order
              </div>
            )}
          </div>
        </div>

        {/* Plans Section */}
        <div className="mt-8 rounded-3xl bg-white shadow-sm border border-neutral-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-neutral-900">Weekly Meal Plans</h2>
            {(() => { 
              try { 
                return localStorage.getItem(`prepmate_selection_vendor_${v.id}`) 
                  ? <span className="text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-full font-medium">Selection completed</span> 
                  : null; 
              } catch { return null; } 
            })()}
          </div>
          
          {offered.length > 0 ? (
            <>
              {/* Plan Cards Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {offered.map((p, idx) => {
                  const isPopular = p.meals === 7 || (offered.length > 0 && !offered.find(x => x.meals === 7) && idx === Math.floor(offered.length / 2));
                  return (
                    <CompactPlanCard
                      key={p.id}
                      plan={p}
                      vendor={v}
                      isPopular={isPopular}
                      isSelected={selectedPlanId === p.id}
                      onSelect={() => setSelectedPlanId(p.id)}
                    />
                  );
                })}
              </div>
              
              {/* CTA Button */}
              <div className="mt-6">
                {!showSelect && !selectedPlanId && (
                  <p className="text-xs text-neutral-400 mb-2">← Select a plan above</p>
                )}
                {showSelect ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => setOpen({ mode:'select' })} 
                      className="flex-1 h-12 rounded-xl bg-[#2a7d4f] text-white hover:bg-[#236840] text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                      data-testid="select-meals-btn"
                    >
                      Choose meals
                    </Button>
                    <Button 
                      onClick={() => setOpen({ mode:'modify' })} 
                      variant="outline" 
                      className="flex-1 h-12 rounded-xl bg-white border-2 border-neutral-200 hover:border-neutral-300 text-base font-semibold transition-all" 
                      data-testid="modify-subscription-btn"
                    >
                      Modify box
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => setOpen({ initialPlanId: selectedPlanId })}
                    disabled={!selectedPlanId}
                    className={`
                      w-full sm:w-auto h-12 px-8 rounded-xl text-base font-semibold transition-all
                      ${selectedPlanId 
                        ? 'bg-[#2a7d4f] text-white hover:bg-[#236840] shadow-lg hover:shadow-xl hover:scale-[1.02]'
                        : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                      }
                    `}
                    data-testid="start-subscription-btn"
                  >
                    {selectedPlanId 
                      ? `Build ${offered.find(p => p.id === selectedPlanId)?.meals || ''}-meal box`
                      : 'Select a plan above'
                    }
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="text-neutral-500">No plans configured yet.</div>
          )}
          
          {isDiagnosticsMode() && (
            <div className="mt-4 text-[11px] text-neutral-400 p-3 bg-neutral-50 rounded-xl">
              Debug: user={user? user.email: 'none'} • hasUserActiveForVendor={String(!!(user && user.active_subscription && user.active_subscription.company_id===v.id))} • fallbackFound={String(!!activeForVendor)} • planId={(user && user.active_subscription && user.active_subscription.company_id===v.id)? user.active_subscription.plan_id : (activeForVendor? activeForVendor.plan_id : '—')}
            </div>
          )}
        </div>

        <div className="mt-4">
          <AllergyWarning />
        </div>

        {/* Menu Section */}
        <div className="mt-8 rounded-3xl bg-white shadow-sm border border-neutral-100 p-6">
          <Tabs defaultValue="menu">
            <TabsList className="bg-neutral-100 p-1 rounded-xl">
              <TabsTrigger value="menu" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">Menu</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">Reviews</TabsTrigger>
            </TabsList>
            <TabsContent value="menu" className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-neutral-600 flex items-center gap-2">
                  <Heart size={14} className="text-red-500" />
                  <span>Favorite meals</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-500">Show favorites only</span>
                  <Switch checked={showFavOnly} onCheckedChange={setShowFavOnly} />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {vendor.menu.filter(it => !showFavOnly || favSet.has(it.id)).map(item => (
                  <div 
                    key={item.id} 
                    className="group flex gap-4 p-4 rounded-2xl bg-neutral-50 hover:bg-white hover:shadow-lg border border-transparent hover:border-neutral-100 transition-all duration-300"
                  >
                    <img 
                      loading="lazy" 
                      src={item.image} 
                      alt={item.name} 
                      className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-neutral-900">{item.name}</h4>
                        {user && (
                          <button 
                            onClick={() => toggleFavMeal(item.id)} 
                            className="ml-2 text-neutral-400 hover:text-red-500 transition-colors"
                          >
                            <Heart size={18} className={favSet.has(item.id) ? 'text-red-500 fill-red-500' : ''} />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{item.ingredients || item.desc || '—'}</p>
                      {item.calories && (
                        <div className="mt-2 inline-flex items-center px-2 py-1 bg-white rounded-lg text-xs text-neutral-600">
                          {item.calories} cal
                          {item.macros && ` • ${item.macros.protein_g || 0}g protein`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="reviews" className="mt-6 space-y-4">
              {vendor.reviews.map((r, idx) => (
                <div key={idx} className="p-4 rounded-2xl bg-neutral-50">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-semibold text-neutral-600">
                      {r.user_name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-neutral-900">{r.user_name}</div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} size={12} className={i <= r.stars ? "fill-amber-400 text-amber-400" : "text-neutral-300"} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mt-3">{r.text}</p>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      {open && (
        typeof open === 'object' ? (
          <WeeklySubscription 
            vendor={v} 
            menu={vendor.menu} 
            open={!!open} 
            onOpenChange={(val) => { setOpen(val); if (!val) setSelectedPlanId(null); }} 
            user={user} 
            mode={open.mode || 'start'} 
            initialPlanId={open.initialPlanId || user?.active_subscription?.plan_id || null} 
          />
        ) : (
          <WeeklySubscription 
            vendor={v} 
            menu={vendor.menu} 
            open={!!open} 
            onOpenChange={(val) => { setOpen(val); if (!val) setSelectedPlanId(null); }} 
            user={user} 
          />
        )
      )}
    </div>
  );
}
